import { useState } from "react";
import { X, Send, MessageCircle, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { REQUIREMENT_OPTIONS, CUSTOMER_TYPES } from "@/data/content";

export default function QuoteModal() {
  const { quoteOpen, setQuoteOpen, quoteContext, waLink } = useSite();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  if (!quoteOpen) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name || !form.mobile) { setErr("Name and Mobile are required."); return; }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source: `Quote Modal - ${quoteContext}` });
      setDone(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Submission failed.");
    } finally { setLoading(false); }
  };

  const close = () => { setQuoteOpen(false); setTimeout(() => { setDone(false); setForm({}); setErr(""); }, 300); };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="quote-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-lg card-surface !bg-[#0B1522] p-6 sm:p-8 max-h-[92vh] overflow-y-auto">
        <button onClick={close} className="absolute top-4 right-4 text-slate-400 hover:text-white" data-testid="quote-modal-close"><X size={22} /></button>
        {!done ? (
          <>
            <div className="mono-label">Get a Quote</div>
            <h3 className="font-display text-2xl font-bold text-white mt-1 mb-1">Submit Your Requirement</h3>
            <p className="text-sm text-slate-400 mb-5">Share your requirement and our team will help identify the right solution.</p>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="quote-form-name" placeholder="Name *" onChange={set("name")} className="qf" />
                <input data-testid="quote-form-mobile" placeholder="Mobile *" onChange={set("mobile")} className="qf" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="quote-form-email" placeholder="Email" onChange={set("email")} className="qf" />
                <input data-testid="quote-form-company" placeholder="Company" onChange={set("company")} className="qf" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="quote-form-city" placeholder="City" onChange={set("city")} className="qf" />
                <select data-testid="quote-form-customer-type" onChange={set("customer_type")} className="qf" defaultValue="">
                  <option value="" disabled>Customer Type</option>
                  {CUSTOMER_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="quote-form-requirement" onChange={set("requirement")} className="qf" defaultValue="">
                  <option value="" disabled>Product / Service</option>
                  {REQUIREMENT_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <input data-testid="quote-form-capacity" placeholder="Approx. Capacity" onChange={set("capacity")} className="qf" />
              </div>
              <textarea data-testid="quote-form-message" placeholder="Requirement details" rows={3} onChange={set("message")} className="qf resize-none" />
              {err && <div className="text-red-400 text-sm" data-testid="quote-form-error">{err}</div>}
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="quote-form-submit">
                <Send size={16} /> {loading ? "Submitting..." : "Submit Requirement"}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-8" data-testid="quote-success">
            <CheckCircle2 className="mx-auto text-emerald-400 mb-4" size={56} />
            <h3 className="font-display text-2xl font-bold text-white">Requirement Received</h3>
            <p className="text-slate-400 mt-2 mb-6">Thank you. Our team will get in touch with you shortly.</p>
            <a href={waLink()} target="_blank" rel="noreferrer" className="btn-primary" data-testid="quote-success-whatsapp"><MessageCircle size={16} /> Continue on WhatsApp</a>
          </div>
        )}
      </div>
      <style>{`.qf{width:100%;background:#060B11;border:1px solid rgba(255,255,255,.1);border-radius:.6rem;padding:.7rem .9rem;color:#E2E8F0;font-size:.9rem;outline:none}.qf:focus{border-color:#10B981}`}</style>
    </div>
  );
}
